import { resolveRpqStatus } from "./clientRpqStatus";

export const RPQ_FUND_PURCHASE_ERROR =
  "Fund risk rating exceeds the client's RPQ risk profile.";

export const RPQ_MISSING_WARNING =
  "Client has no RPQ. All funds are shown. Please confirm suitability before recommending.";

export const RPQ_EXPIRED_WARNING =
  "Client RPQ has expired. All funds are shown. Please confirm suitability before recommending.";

export const FUND_MISSING_RISK_RATING_WARNING =
  "This fund has no risk rating. Please confirm it is suitable for the client's RPQ.";

const RESTRICTING_RPQ_STATUSES = new Set([
  "VALID",
  "EXPIRING_1W",
  "EXPIRING_1M",
]);

const VALID_RPQ_LEVELS = new Set([1, 2, 3, 4, 5]);

export const normalizeLatestRpqLevel = (value) => {
  if (value == null || value === "") return null;
  const num = Number(value);
  if (!VALID_RPQ_LEVELS.has(num)) return null;
  return num;
};

export const parseFundRiskRating = (fundOrProduct = {}) => {
  const raw = fundOrProduct?.riskRating;
  if (raw === null || raw === undefined || raw === "") return null;
  const parsed = Number(raw);
  return Number.isNaN(parsed) ? null : parsed;
};

export const parseClientRpqRiskLevel = (source = {}) => {
  const rpq = source?.rpq?.length ? source.rpq : source?.client?.rpq ?? [];
  const level = rpq[0]?.riskLevel;
  if (level === null || level === undefined || level === "") return null;
  const parsed = Number(level);
  return Number.isNaN(parsed) ? null : parsed;
};

const resolveRpqRecords = (orderOrTransaction = {}) => {
  const order = orderOrTransaction.orderId ?? orderOrTransaction;
  const client = order?.client ?? orderOrTransaction.client ?? {};
  return order?.rpq?.length
    ? order.rpq
    : client?.rpq ?? orderOrTransaction.rpq ?? [];
};

export const shouldRestrictFundsByRpq = (status) =>
  RESTRICTING_RPQ_STATUSES.has(status);

export const resolveClientRpqFundContext = (orderOrTransaction = {}) => {
  const rpq = resolveRpqRecords(orderOrTransaction);
  const order = orderOrTransaction.orderId ?? orderOrTransaction;
  const client = order?.client ?? orderOrTransaction.client ?? {};
  const status = rpq.length
    ? resolveRpqStatus(rpq)
    : order?.rpqStatus ??
      client?.rpqStatus ??
      orderOrTransaction.rpqStatus ??
      "NONE";
  const riskLevel = parseClientRpqRiskLevel({ rpq, client });

  return {
    riskLevel,
    status,
    restrictFunds: shouldRestrictFundsByRpq(status) && riskLevel != null,
  };
};

export const resolveClientRpqRiskLevel = (orderOrTransaction = {}) =>
  resolveClientRpqFundContext(orderOrTransaction).riskLevel;

/**
 * Fund ceiling for an investment transaction.
 * Uses the selected Latest RPQ Level when the client RPQ still restricts funds.
 */
export const resolveArFormRpqFundContext = (orderOrTransaction = {}) => {
  const context = resolveClientRpqFundContext(orderOrTransaction);
  if (!context.restrictFunds) return context;
  const latestLevel = normalizeLatestRpqLevel(
    orderOrTransaction?.arForm?.latestRpqLevel
  );
  if (latestLevel == null) return context;
  return { ...context, riskLevel: latestLevel };
};

export const getRpqFundListWarning = (context = {}) => {
  if (context.status === "NONE") return RPQ_MISSING_WARNING;
  if (context.status === "EXPIRED") return RPQ_EXPIRED_WARNING;
  return null;
};

export const getFundMissingRiskRatingWarning = (fundOrProduct = {}) =>
  parseFundRiskRating(fundOrProduct) == null
    ? FUND_MISSING_RISK_RATING_WARNING
    : null;

export const isFundEligibleForClientRpq = (fund, contextOrLevel) => {
  const context =
    contextOrLevel && typeof contextOrLevel === "object"
      ? contextOrLevel
      : {
          riskLevel: contextOrLevel,
          restrictFunds: contextOrLevel != null,
        };
  if (!context.restrictFunds) return true;
  const fundRisk = parseFundRiskRating(fund);
  if (fundRisk == null) return true;
  return fundRisk <= context.riskLevel;
};

export const filterFundsByClientRpq = (funds = [], contextOrLevel) => {
  const context =
    contextOrLevel && typeof contextOrLevel === "object"
      ? contextOrLevel
      : {
          riskLevel: contextOrLevel,
          restrictFunds: contextOrLevel != null,
        };
  if (!context.restrictFunds) return funds;
  return funds.filter((fund) => isFundEligibleForClientRpq(fund, context));
};

export const getRpqFundPurchaseError = (contextOrLevel, products = []) => {
  const context =
    contextOrLevel && typeof contextOrLevel === "object"
      ? contextOrLevel
      : {
          riskLevel: contextOrLevel,
          restrictFunds: contextOrLevel != null,
        };
  if (!context.restrictFunds) return null;
  const list = Array.isArray(products) ? products : [products];
  const blocked = list.some((product) => {
    const risk = parseFundRiskRating(product);
    return risk != null && risk > context.riskLevel;
  });
  return blocked ? RPQ_FUND_PURCHASE_ERROR : null;
};
